import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Menu,
  MenuItem,
  TextField,
  ThemeProvider as MuiThemeProvider,
  Typography,
} from '@mui/material';
import {
  ArrowForwardRounded,
  ExpandMoreRounded,
  Language,
  LockOutlined,
  MailOutlineRounded,
  ShieldOutlined,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/authService';
import { createAppTheme } from '../../theme';
import { ROOT_DOMAIN, getSubdomain } from '../../utils/subdomain';
import {
  auth as T,
  authThemeConfig,
  focusVisibleRing,
  glassCard,
  gradientText,
  reducedMotion,
  riseIn,
} from '../../components/auth/authTokens';
import AuthBackdrop from '../../components/auth/AuthBackdrop';
import LaunchOverlay from '../../components/auth/LaunchOverlay';
import ModuleOrbit from '../../components/auth/ModuleOrbit';
import StratifyGlyph from '../../components/auth/StratifyGlyph';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Where "remember me" keeps the last-used work email.
 *
 * Scope note: this stores the *email only*, never a credential or a token.
 * There is no server-side long-lived session to opt into yet, so the checkbox
 * promises exactly what it does — skip retyping your address — rather than
 * implying a persistent login the backend does not issue.
 */
const REMEMBER_EMAIL_KEY = 'stratify.login.rememberedEmail';

const readRememberedEmail = (): string => {
  try {
    return window.localStorage.getItem(REMEMBER_EMAIL_KEY) ?? '';
  } catch {
    // Private-mode Safari throws on localStorage access.
    return '';
  }
};

/** The four proof points under the hero copy. Two lines each, by design. */
const TRUST_ITEMS: Array<[string, string]> = [
  ['All-in-One', 'Platform'],
  ['Real-time', 'Insights'],
  ['Built for', 'Scale'],
  ['Trusted by', 'Growing Enterprises'],
];

/**
 * Staggered entrance. A plain CSS animation rather than the scroll-driven
 * `Reveal` helper: every element here is above the fold on load, so an
 * IntersectionObserver would only add a frame of latency to something that is
 * always already visible.
 */
const rise = (delay: number) => ({
  animation: `${riseIn} .7s cubic-bezier(.2,.7,.2,1) ${delay}s both`,
  ...reducedMotion,
});

/** Label tied to its input by `htmlFor`, so the whole label is a click target. */
const FieldLabel: React.FC<{ htmlFor: string; children: React.ReactNode }> = ({ htmlFor, children }) => (
  <Typography
    component="label"
    htmlFor={htmlFor}
    sx={{ display: 'block', mb: 0.75, fontSize: '0.8125rem', fontWeight: 600, color: T.textMuted }}
  >
    {children}
  </Typography>
);

const LoginPage: React.FC = () => {
  // `email` seeds from the remembered address so a returning user lands on the
  // password field with one keystroke of work left.
  const [email, setEmail] = useState(readRememberedEmail);
  const [remember, setRemember] = useState(() => Boolean(readRememberedEmail()));
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotStatus, setForgotStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [forgotError, setForgotError] = useState('');
  const [phase, setPhase] = useState<'idle' | 'authing' | 'ready'>('idle');
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);
  const { login, isLoading, error } = useAuth();

  // A fixed Stratify theme, so MUI surfaces we do not style by hand (the reset
  // dialog, alerts) ignore whatever tenant preset sits in localStorage.
  const authTheme = useMemo(() => createAppTheme(authThemeConfig), []);
  const workspace = getSubdomain();
  const ready = Boolean(email && password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(email, password, async () => {
        // credentials accepted — play the launch transition (~2s) before handoff
        setPhase('authing');
        await sleep(1250);
        setPhase('ready');
        await sleep(800);
      });
      // auth state now flips → the router transitions to the dashboard.

      // Only persist on success: remembering an address that was just rejected
      // would prefill a typo forever.
      try {
        if (remember) window.localStorage.setItem(REMEMBER_EMAIL_KEY, email);
        else window.localStorage.removeItem(REMEMBER_EMAIL_KEY);
      } catch {
        /* storage unavailable — the convenience is optional, the login is not */
      }
    } catch {
      setPhase('idle'); // wrong password fails fast, before the overlay
    }
  };

  /**
   * Shared styling for the two credential inputs. Declared once here rather
   * than per field so the focus ring, the autofill override and the disabled
   * state can never drift apart between email and password.
   */
  const darkField = {
    '& .MuiOutlinedInput-root': {
      color: T.text,
      backgroundColor: T.fieldFill,
      borderRadius: '12px',
      fontSize: '0.9375rem',
      transition: 'box-shadow .2s, background-color .2s',
      '& fieldset': { borderColor: T.hairline, transition: 'border-color .2s' },
      '&:hover fieldset': { borderColor: T.hairlineStrong },
      '&.Mui-focused': { backgroundColor: T.fieldFillFocus, boxShadow: T.focusRing },
      '&.Mui-focused fieldset': { borderColor: T.accent, borderWidth: '1px' },
      '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.02)' },
    },
    '& .MuiOutlinedInput-input::placeholder': { color: T.textFaint, opacity: 1 },
    // Chrome paints autofilled inputs with a hard-coded pale yellow that no
    // background rule can beat; a huge inset shadow is the only way to hide it.
    '& input:-webkit-autofill': {
      WebkitBoxShadow: `0 0 0 100px ${T.inkMid} inset`,
      WebkitTextFillColor: T.text,
      caretColor: T.text,
      borderRadius: 'inherit',
    },
    '& .MuiInputAdornment-root': { color: T.textFaint },
  } as const;

  return (
    <MuiThemeProvider theme={authTheme}>
      {phase !== 'idle' && <LaunchOverlay phase={phase === 'ready' ? 'ready' : 'authing'} />}

      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          overflowX: 'hidden',
          overflowY: 'auto',
          backgroundColor: T.ink,
          background: T.pageGradient,
          color: T.text,
        }}
      >
        <AuthBackdrop />

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            minHeight: '100%',
            maxWidth: T.maxWidth,
            mx: 'auto',
            px: { xs: 2.5, sm: 4, lg: 5 },
            py: { xs: 3, md: 4 },
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 4, md: 5 },
          }}
        >
          {/* ================= TOP BAR ================= */}
          <Box
            component="header"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              ...rise(0),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0 }}>
              <StratifyGlyph size={46} />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="span"
                  sx={{
                    display: 'block',
                    fontSize: { xs: '1.15rem', sm: '1.4rem' },
                    fontWeight: 600,
                    letterSpacing: '-0.02em',
                    color: T.text,
                    lineHeight: 1.15,
                  }}
                >
                  Stratify ERP
                </Typography>
                <Typography component="span" sx={{ display: 'block', fontSize: '0.72rem', color: T.textFaint, mt: '2px' }}>
                  Powered by Fensso Tech LLP
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: T.textMuted, lineHeight: 1.35 }}>
                Smarter Businesses
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: T.textMuted, lineHeight: 1.35 }}>
                A Brighter Tomorrow
              </Typography>
              <Box
                sx={{
                  ml: 'auto',
                  mt: 1,
                  width: 52,
                  height: 2,
                  borderRadius: 1,
                  background: T.headlineGradient,
                }}
              />
            </Box>
          </Box>

          {/* ================= MAIN ================= */}
          <Box
            component="main"
            sx={{
              flex: 1,
              display: 'grid',
              // The split only turns on at lg: between md and lg a 42% card is
              // too narrow for a comfortable form, and the orbit is too wide to
              // share the row with it.
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 58fr) minmax(0, 42fr)' },
              alignItems: 'center',
              columnGap: { lg: 7 },
              rowGap: { xs: 5, md: 6 },
            }}
          >
            {/* ---------- LEFT: the story ---------- */}
            <Box sx={{ order: { xs: 2, lg: 1 }, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: T.textFaint,
                  ...rise(0.08),
                }}
              >
                Plan&nbsp;&nbsp;Operate&nbsp;&nbsp;Connect&nbsp;&nbsp;Grow
              </Typography>

              <Typography
                component="h1"
                sx={{
                  mt: 2,
                  fontSize: 'clamp(2.1rem, 4.4vw, 3.2rem)',
                  fontWeight: 700,
                  lineHeight: 1.1,
                  letterSpacing: '-0.03em',
                  color: T.text,
                  ...rise(0.14),
                }}
              >
                Welcome to the
                <br />
                <Box component="span" sx={gradientText}>
                  intelligence layer
                </Box>
                <br />
                of your business.
              </Typography>

              <Typography
                sx={{
                  mt: 2.5,
                  maxWidth: '34rem',
                  fontSize: '1rem',
                  lineHeight: 1.7,
                  color: T.textMuted,
                  ...rise(0.2),
                }}
              >
                Connect finance, sales, operations, supply chain, HR, and AI in one unified platform. Turn data into
                decisions and possibilities into progress.
              </Typography>

              {/* Trust row — thin rules between items, not around them. */}
              <Box
                sx={{
                  mt: 3.5,
                  display: 'flex',
                  flexWrap: 'wrap',
                  rowGap: 2,
                  maxWidth: '36rem',
                  ...rise(0.26),
                }}
              >
                {TRUST_ITEMS.map(([top, bottom], i) => (
                  <Box
                    key={top + bottom}
                    sx={{
                      pr: { xs: 2, sm: 2.5 },
                      pl: i === 0 ? 0 : { xs: 2, sm: 2.5 },
                      borderLeft: i === 0 ? 'none' : `1px solid ${T.hairline}`,
                    }}
                  >
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: T.text, lineHeight: 1.35 }}>
                      {top}
                    </Typography>
                    <Typography sx={{ fontSize: '0.8125rem', color: T.textFaint, lineHeight: 1.35 }}>
                      {bottom}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* The hero visual. */}
              <Box sx={{ mt: { xs: 4, lg: 2 }, ...rise(0.34) }}>
                <ModuleOrbit />
              </Box>

              <Box sx={{ mt: { xs: 4, lg: 2.5 }, ...rise(0.42) }}>
                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: T.accent }}>
                  One business. One platform. A smarter tomorrow.
                </Typography>
                <Box sx={{ mt: 1, width: 64, height: 2, borderRadius: 1, background: T.headlineGradient }} />
              </Box>
            </Box>

            {/* ---------- RIGHT: the login card ---------- */}
            <Box
              sx={{
                order: { xs: 1, lg: 2 },
                justifySelf: { xs: 'center', lg: 'end' },
                width: '100%',
                maxWidth: 460,
                minWidth: 0,
                ...rise(0.1),
              }}
            >
              <Box sx={{ ...glassCard, p: { xs: 2.75, sm: 4 } }}>
                {/* Language selector. Presentational only: the app ships no i18n
                    layer yet, so this advertises the slot without promising a
                    translation that does not exist. */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                  <Button
                    size="small"
                    aria-haspopup="menu"
                    aria-expanded={Boolean(langAnchor)}
                    onClick={(e) => setLangAnchor(e.currentTarget)}
                    endIcon={<ExpandMoreRounded />}
                    sx={{
                      color: T.textFaint,
                      fontSize: '0.78rem',
                      fontWeight: 500,
                      textTransform: 'none',
                      minWidth: 0,
                      px: 1,
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)', color: T.text },
                      ...focusVisibleRing,
                    }}
                  >
                    English
                  </Button>
                  <Menu
                    anchorEl={langAnchor}
                    open={Boolean(langAnchor)}
                    onClose={() => setLangAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <MenuItem selected onClick={() => setLangAnchor(null)}>
                      English
                    </MenuItem>
                  </Menu>
                </Box>

                <Typography
                  component="h2"
                  sx={{ fontSize: '1.9rem', fontWeight: 700, letterSpacing: '-0.025em', color: T.text, lineHeight: 1.15 }}
                >
                  Welcome back
                </Typography>
                <Typography sx={{ mt: 1, fontSize: '0.9rem', lineHeight: 1.6, color: T.textMuted }}>
                  Sign in to Stratify ERP and keep your business moving forward.
                </Typography>

                {/* Which workspace host this login targets — the one piece of
                    tenant context a user cannot infer from the form itself. */}
                {workspace && (
                  <Box
                    sx={{
                      mt: 2,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1.25,
                      py: 0.5,
                      borderRadius: 999,
                      border: `1px solid ${T.hairline}`,
                      backgroundColor: 'rgba(255,255,255,0.04)',
                      color: T.textMuted,
                    }}
                  >
                    <Language sx={{ fontSize: 14 }} />
                    <Typography sx={{ fontSize: '0.75rem', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                      {`${workspace}.${ROOT_DOMAIN}`}
                    </Typography>
                  </Box>
                )}

                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      mt: 2.5,
                      borderRadius: '12px',
                      backgroundColor: 'rgba(220,38,38,0.12)',
                      color: '#fecaca',
                      border: '1px solid rgba(220,38,38,0.35)',
                      '& .MuiAlert-icon': { color: '#f87171' },
                    }}
                  >
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
                  <FieldLabel htmlFor="email">Work email</FieldLabel>
                  <TextField
                    fullWidth
                    required
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    placeholder="you@yourcompany.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    sx={{ ...darkField, mb: 2.25 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MailOutlineRounded sx={{ fontSize: 19 }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <FieldLabel htmlFor="password">Password</FieldLabel>
                  <TextField
                    fullWidth
                    required
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    sx={darkField}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined sx={{ fontSize: 19 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            onClick={() => setShowPassword((v) => !v)}
                            edge="end"
                            size="small"
                            sx={{ color: T.textFaint, ...focusVisibleRing }}
                          >
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box
                    sx={{
                      mt: 1.5,
                      mb: 2.75,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: '-9px' }}>
                      <Checkbox
                        id="remember"
                        name="remember"
                        size="small"
                        checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                        sx={{
                          color: T.hairlineStrong,
                          '&.Mui-checked': { color: T.accent },
                          ...focusVisibleRing,
                        }}
                      />
                      <Typography
                        component="label"
                        htmlFor="remember"
                        sx={{ fontSize: '0.8125rem', color: T.textMuted, cursor: 'pointer', userSelect: 'none' }}
                      >
                        Remember me
                      </Typography>
                    </Box>

                    <MuiLink
                      component="button"
                      type="button"
                      onClick={() => {
                        setForgotEmail(email);
                        setForgotStatus('idle');
                        setForgotError('');
                        setForgotOpen(true);
                      }}
                      underline="hover"
                      sx={{
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        color: T.accent,
                        background: 'none',
                        border: 0,
                        p: 0,
                        cursor: 'pointer',
                        ...focusVisibleRing,
                      }}
                    >
                      Forgot password?
                    </MuiLink>
                  </Box>

                  <Button
                    type="submit"
                    fullWidth
                    disabled={isLoading || !ready}
                    endIcon={!isLoading ? <ArrowForwardRounded /> : undefined}
                    sx={{
                      height: 52,
                      fontSize: '0.9375rem',
                      fontWeight: 700,
                      letterSpacing: '0.01em',
                      textTransform: 'none',
                      borderRadius: '12px',
                      color: '#ffffff',
                      background: T.buttonGradient,
                      backgroundSize: '200% auto',
                      boxShadow: '0 10px 28px rgba(91,91,240,0.34)',
                      transition: 'background-position .45s ease, box-shadow .25s ease, transform .18s ease',
                      '&:hover': {
                        backgroundPosition: 'right center',
                        boxShadow: '0 16px 38px rgba(109,92,245,0.48)',
                        transform: 'translateY(-2px)',
                      },
                      '&:active': { transform: 'translateY(0)' },
                      '&.Mui-disabled': {
                        background: 'rgba(255,255,255,0.07)',
                        color: 'rgba(203,216,245,0.38)',
                        boxShadow: 'none',
                      },
                      ...reducedMotion,
                      ...focusVisibleRing,
                    }}
                  >
                    {isLoading ? (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={18} sx={{ color: '#ffffff' }} /> Authenticating…
                      </Box>
                    ) : (
                      'Sign in'
                    )}
                  </Button>
                </Box>

                {/* Divider. Hand-built rather than MUI's <Divider>, which cannot
                    tint its two rules independently of the label. */}
                <Box sx={{ my: 2.75, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ flex: 1, height: 1, backgroundColor: T.hairline }} />
                  <Typography sx={{ fontSize: '0.72rem', color: T.textFaint, whiteSpace: 'nowrap' }}>
                    or continue with
                  </Typography>
                  <Box sx={{ flex: 1, height: 1, backgroundColor: T.hairline }} />
                </Box>

                {/*
                  The only single-sign-on the platform actually has is the
                  LMS → ERP token handoff behind /sso-login. It is deliberately
                  NOT branded as Microsoft here: no Entra/Microsoft flow exists,
                  and a Microsoft button that lands on a generic token page is a
                  promise the backend cannot keep.
                  TODO: relabel + add the Microsoft mark once an actual OIDC
                  provider is wired in authService.
                */}
                <Button
                  component={RouterLink}
                  to="/sso-login"
                  fullWidth
                  variant="outlined"
                  startIcon={<ShieldOutlined sx={{ fontSize: 18 }} />}
                  sx={{
                    height: 48,
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    textTransform: 'none',
                    borderRadius: '12px',
                    color: T.text,
                    borderColor: T.hairline,
                    backgroundColor: 'rgba(255,255,255,0.02)',
                    '&:hover': { borderColor: T.hairlineStrong, backgroundColor: 'rgba(255,255,255,0.06)' },
                    ...focusVisibleRing,
                  }}
                >
                  Continue with single sign-on
                </Button>

                {/* Enterprise assurance */}
                <Box
                  sx={{
                    mt: 3,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.25,
                    p: 1.5,
                    borderRadius: '12px',
                    border: `1px solid ${T.hairline}`,
                    backgroundColor: 'rgba(255,255,255,0.025)',
                  }}
                >
                  <ShieldOutlined sx={{ fontSize: 18, color: T.accent, mt: '1px', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.55, color: T.textMuted }}>
                    <Box component="span" sx={{ fontWeight: 700, color: T.text }}>
                      Enterprise-grade security.
                    </Box>{' '}
                    Your data is protected by industry-leading security standards.
                  </Typography>
                </Box>

                {/* Card footer */}
                <Box
                  sx={{
                    mt: 2.5,
                    pt: 2,
                    borderTop: `1px solid ${T.hairline}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography sx={{ fontSize: '0.78rem', color: T.textFaint }}>
                    New to Stratify ERP?{' '}
                    {workspace ? (
                      // On a tenant host the right next step is finding your own
                      // workspace, not creating a second one.
                      <MuiLink href={`https://${ROOT_DOMAIN}`} underline="hover" sx={{ color: T.accent, fontWeight: 600 }}>
                        Find the right one
                      </MuiLink>
                    ) : (
                      <MuiLink
                        component={RouterLink}
                        to="/signup"
                        underline="hover"
                        sx={{ color: T.accent, fontWeight: 600, ...focusVisibleRing }}
                      >
                        Create a workspace
                      </MuiLink>
                    )}
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: T.textFaint }}>
                    Need help? Contact your administrator.
                  </Typography>
                </Box>
              </Box>

              {/* Below the card, outside its glass. */}
              <Box
                sx={{
                  mt: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                  ...rise(0.5),
                }}
              >
                <Typography sx={{ fontSize: '0.78rem', fontStyle: 'italic', color: T.textFaint, maxWidth: '22rem' }}>
                  “Technology should simplify business, not complicate it.”
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StratifyGlyph size={22} flat />
                  <Box>
                    <Typography sx={{ fontSize: '0.66rem', color: T.textFaint, lineHeight: 1.25 }}>Powered by</Typography>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: T.textFaint, lineHeight: 1.25 }}>
                      Fensso Tech LLP
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* ================= PAGE FOOTER ================= */}
          <Box
            component="footer"
            sx={{
              pt: 2,
              borderTop: `1px solid ${T.hairline}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
              ...rise(0.56),
            }}
          >
            <Typography sx={{ fontSize: '0.72rem', color: T.textFaint }}>
              © 2026 Fensso Tech LLP. All rights reserved.
            </Typography>
            {/* Plain text, not links: the app has no /privacy, /terms or /support
                route, and a link to nowhere is worse than no link. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              {['Privacy', 'Terms', 'Support'].map((item, i) => (
                <React.Fragment key={item}>
                  {i > 0 && <Box sx={{ width: 1, height: 11, backgroundColor: T.hairline }} />}
                  <Typography sx={{ fontSize: '0.72rem', color: T.textFaint }}>{item}</Typography>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      <Dialog open={forgotOpen} onClose={() => setForgotOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset your password</DialogTitle>
        <DialogContent>
          {forgotStatus === 'sent' ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              If an account exists for that email, a password reset link is on its way.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 0.5 }}>
                Enter the email you sign in with — we'll send a link to reset your password.
              </Typography>
              {forgotStatus === 'error' && (
                <Alert severity="error" sx={{ mb: 2 }}>{forgotError}</Alert>
              )}
              <TextField
                autoFocus
                fullWidth
                type="email"
                label="Email address"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                disabled={forgotStatus === 'sending'}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setForgotOpen(false)}>
            {forgotStatus === 'sent' ? 'Close' : 'Cancel'}
          </Button>
          {forgotStatus !== 'sent' && (
            <Button
              variant="contained"
              disabled={!forgotEmail.trim() || forgotStatus === 'sending'}
              onClick={async () => {
                setForgotStatus('sending');
                setForgotError('');
                try {
                  await authService.requestPasswordReset(forgotEmail.trim());
                  setForgotStatus('sent');
                } catch (err) {
                  setForgotError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
                  setForgotStatus('error');
                }
              }}
            >
              {forgotStatus === 'sending' ? <CircularProgress size={20} color="inherit" /> : 'Send reset link'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </MuiThemeProvider>
  );
};

export default LoginPage;
